import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import { Badge, Button, Spinner } from './ui';
import { formsApi, quizzesApi } from '../lib/api';
import type { ArchivedSubmission, ExportFormat } from '../types/submissions';

/**
 * Submissions tab shared by the Forms and Quizzes editors.
 *
 * Lists index rows from D1 (paginated) and lazily fetches the full archived
 * answer payload from R2 only when a row is expanded — so opening the tab on a
 * quiz with 10k responses costs one small query, not 10k object reads.
 */

type Kind = 'quiz' | 'form';

interface IndexRow {
  id: string;
  data: Record<string, string>;
  resultTitle?: string | null;
  score?: number | null;
  cfContactId: string | null;
  status: string;
  errorMessage: string | null;
  hasAnswers?: boolean;
  createdAt: number;
}

const PAGE_SIZE = 25;

const FORMATS: Array<{ format: ExportFormat; label: string; hint: string }> = [
  { format: 'csv', label: 'CSV', hint: 'One row per submission, one column per question — opens in Excel or Sheets' },
  { format: 'jsonl', label: 'JSONL', hint: 'One full JSON record per line — for scripts and data pipelines' },
  { format: 'json', label: 'JSON', hint: 'A single JSON array of every record' },
];

function timeAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function answerText(a: ArchivedSubmission['answers'][number]): string {
  if (a.textValue) return a.textValue;
  return a.selected.map((s) => s.answerText).join(', ') || '—';
}

export default function SubmissionsPanel({ kind, resourceId }: { kind: Kind; resourceId: string }) {
  const api = kind === 'quiz' ? quizzesApi : formsApi;

  const [rows, setRows] = useState<IndexRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<Record<string, ArchivedSubmission>>({});
  const [loadingDetail, setLoadingDetail] = useState<string | null>(null);

  const [downloading, setDownloading] = useState<ExportFormat | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async (nextOffset: number) => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getSubmissions(resourceId, PAGE_SIZE, nextOffset);
      if (res.success && res.data) {
        setRows(res.data.submissions as unknown as IndexRow[]);
        setTotal(res.data.total ?? res.data.submissions.length);
        setOffset(nextOffset);
      } else {
        setError(res.message || 'Failed to load submissions');
      }
    } catch {
      setError('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  }, [api, resourceId]);

  useEffect(() => { void load(0); }, [load]);

  const toggle = async (id: string, hasAnswers?: boolean) => {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (!hasAnswers || detail[id]) return;

    setLoadingDetail(id);
    try {
      const res = await api.getSubmission(resourceId, id);
      if (res.success && res.data) {
        setDetail((prev) => ({ ...prev, [id]: res.data!.submission }));
      }
    } finally {
      setLoadingDetail(null);
    }
  };

  const download = async (format: ExportFormat) => {
    setDownloading(format);
    setError(null);
    const res = await api.exportSubmissions(resourceId, format, {
      from: from || undefined,
      to: to || undefined,
    });
    if (!res.ok) setError(res.message);
    setDownloading(null);
  };

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  return (
    <div>
      {/* ── Export bar ── */}
      <div className="border border-line rounded-lg p-3 mb-4 bg-surface-2">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-ink-2 mb-1">From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="ds-input text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-ink-2 mb-1">To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="ds-input text-sm"
            />
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {FORMATS.map(({ format, label, hint }) => (
              <Button
                key={format}
                variant="ghost"
                icon={Download}
                title={hint}
                disabled={downloading !== null}
                onClick={() => void download(format)}
              >
                {downloading === format ? 'Preparing…' : label}
              </Button>
            ))}
          </div>
        </div>
        <p className="text-xs text-ink-3 mt-2">
          Downloads every archived {kind === 'quiz' ? 'response' : 'submission'} in the date range
          {from || to ? '' : ' (all time)'} — not just the page below.
        </p>
      </div>

      {error && <p className="text-sm text-danger mb-3">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-8"><Spinner className="!h-6 !w-6" /></div>
      ) : rows.length === 0 ? (
        <p className="text-ink-3 text-sm text-center py-8">No submissions yet.</p>
      ) : (
        <>
          <div className="space-y-2">
            {rows.map((sub) => {
              const open = expandedId === sub.id;
              const record = detail[sub.id];
              return (
                <div key={sub.id} className="border border-line rounded-lg">
                  <button
                    type="button"
                    onClick={() => void toggle(sub.id, sub.hasAnswers)}
                    className="w-full text-left p-3 flex items-center justify-between gap-3"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {open ? <ChevronDown className="w-4 h-4 text-ink-3 shrink-0" /> : <ChevronRight className="w-4 h-4 text-ink-3 shrink-0" />}
                      <Badge tone={sub.status === 'success' ? 'success' : 'danger'}>{sub.status}</Badge>
                      <span className="text-sm text-ink truncate">{sub.data?.email || 'Unknown'}</span>
                      {sub.resultTitle && <span className="text-xs bg-accent-soft text-accent-ink px-2 py-0.5 rounded shrink-0">{sub.resultTitle}</span>}
                      {sub.score !== null && sub.score !== undefined && <span className="text-xs text-ink-3 shrink-0">Score: {sub.score}</span>}
                      {sub.cfContactId && <span className="text-xs font-mono text-ink-3 shrink-0">CF #{sub.cfContactId}</span>}
                    </div>
                    <span className="text-xs text-ink-3 shrink-0">{timeAgo(sub.createdAt)}</span>
                  </button>

                  {sub.errorMessage && <p className="text-xs text-danger px-3 pb-2">{sub.errorMessage}</p>}

                  {open && (
                    <div className="border-t border-line p-3">
                      {!sub.hasAnswers ? (
                        <p className="text-xs text-ink-3">
                          This submission was recorded before answers were archived, so only the summary above exists.
                        </p>
                      ) : loadingDetail === sub.id ? (
                        <div className="flex justify-center py-4"><Spinner className="!h-5 !w-5" /></div>
                      ) : !record ? (
                        <p className="text-xs text-ink-3">Could not load the archived answers.</p>
                      ) : (
                        <div className="space-y-3">
                          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                            <div><dt className="text-ink-3">First name</dt><dd className="text-ink">{record.lead.firstName || '—'}</dd></div>
                            <div><dt className="text-ink-3">Last name</dt><dd className="text-ink">{record.lead.lastName || '—'}</dd></div>
                            <div><dt className="text-ink-3">Phone</dt><dd className="text-ink">{record.lead.phone || '—'}</dd></div>
                            <div><dt className="text-ink-3">Country</dt><dd className="text-ink">{record.meta.country || '—'}</dd></div>
                          </dl>

                          <div className="border border-line rounded overflow-hidden">
                            <table className="w-full text-xs">
                              <tbody>
                                {record.answers.length === 0 ? (
                                  <tr><td className="p-2 text-ink-3">No answers recorded.</td></tr>
                                ) : record.answers.map((a) => (
                                  <tr key={a.questionId} className="border-b border-line last:border-0">
                                    <td className="p-2 text-ink-2 align-top w-1/2">{a.questionText}</td>
                                    <td className="p-2 text-ink align-top whitespace-pre-wrap break-words">{answerText(a)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {record.meta.pageUrl && (
                            <p className="text-xs text-ink-3 break-all">Submitted from {record.meta.pageUrl}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-xs text-ink-3">
              {total.toLocaleString()} total · page {page} of {pages}
            </span>
            <div className="flex items-center gap-2">
              <Button variant="ghost" icon={RefreshCw} onClick={() => void load(offset)} disabled={loading}>Refresh</Button>
              <Button variant="ghost" onClick={() => void load(Math.max(offset - PAGE_SIZE, 0))} disabled={offset === 0 || loading}>Previous</Button>
              <Button variant="ghost" onClick={() => void load(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= total || loading}>Next</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
